import { NineActions, NineButton } from "@9amhealth/wcl/generated/react";
import styled from "@emotion/styled";
import type { FC } from "react";
import React, { useEffect } from "react";
import {
  IconLargePlusPharmacyMedicalCupSnakeBowlOfHygieia,
  IconPharmacyStorefront,
  IconEditModify
} from "src/constants/icons";
import { formatPhoneNumberNational } from "src/lib/formatPhoneNumber";
import formatZipCode from "src/lib/formatZipCode";
import toTitleCase from "src/lib/toTitleCase";
import PatientPharmacyBloc from "src/state/PatientPharmacyBloc/PatientPharmacyBloc";
import { useBloc } from "src/state/state";
import AppPageTitle from "src/ui/components/AppPageTitle/AppPageTitle";
import AsyncContent from "src/ui/components/AsyncContent/AsyncContent";
import Data from "src/ui/components/Data/Data";
import { PROFILE_URL } from "src/ui/components/ProfileOverview/ProfileOverview";
import OverlayBackButton from "src/ui/components/StyledComponents/OverlayBackButton";
import {
  AppPopup,
  useAppQueryPopup
} from "../AppQueryPopups/AppQueryPopupsBloc";
import Translate from "src/ui/components/Translate/Translate";

const Title = styled.p`
  margin-bottom: 0.5rem;
`;

const PharmacyTile = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: var(--color-white);
  border-radius: 0.5rem;
  box-shadow: var(--light-shadow);
  cursor: pointer;
  transition: transform 0.1s ease;
`;

// Pastel sunrise gradient behind the pharmacy icon (Figma node 5805:93849) — the same
// diagonal 229° gradient the task-list thumbnails use (TaskListItem.tsx).
const PHARMACY_ICON_GRADIENT =
  "linear-gradient(229deg, #ffdfb4 -17.69%, #fddee9 33.52%, #c1d7fb 81.64%)";

const PharmacyIcon = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 0.5rem;
  background: ${PHARMACY_ICON_GRADIENT};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 1.5rem;
    height: 1.5rem;
    g {
      fill-opacity: 1;
    }
  }
`;

const PharmacyContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const PharmacyName = styled.span`
  line-height: 1;
`;

const PharmacyAddress = styled.span`
  color: var(--color-charcoal-80);
`;

const PharmacyPhone = styled.span`
  /* 12px below the address: the 2px PharmacyContent flex gap + 10px here. */
  margin-top: 0.625rem;
  color: var(--Accessible-Blue, #3973da);
  font-weight: 500;
  text-decoration: underline;
`;

const PharmacyEndIcon = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  /* IconEditModify hardcodes a light-gray (#E6E3DB) stroke that's barely visible; recolor to
     primary gray per design review. */
  svg path {
    stroke: var(--color-gray);
  }
`;

export const PlaceholderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const PROFILE_PHARMACY_INFO_ROUTE = "/app/profile/pharmacy";

interface PharmacyInformationDetailProps {
  compact?: boolean;
  descriptionText?: string;
  /**
   * Hide the built-in "Your pharmacy" title (compact mode) when a parent renders its own
   * section header, so the title isn't shown twice.
   */
  hideTitle?: boolean;
}

const PharmacyInformationDetail: FC<PharmacyInformationDetailProps> = ({
  compact = false,
  descriptionText,
  hideTitle = false
}) => {
  const [{ loading, preferredPharmacy }, { loadPreferredPharmacy }] =
    useBloc(PatientPharmacyBloc);

  useEffect(() => {
    void loadPreferredPharmacy();
  }, []);

  const [openSelect] = useAppQueryPopup(AppPopup.profileSelectPharmacy, {
    onClose: () => {
      void loadPreferredPharmacy();
    }
  });

  const formatPharmacyAddress = () => {
    if (!preferredPharmacy) {
      return "";
    }

    const addressParts = [
      toTitleCase(preferredPharmacy.address1 || ""),
      preferredPharmacy.address2
        ? toTitleCase(preferredPharmacy.address2)
        : null,
      toTitleCase(preferredPharmacy.city || ""),
      preferredPharmacy.state,
      formatZipCode(preferredPharmacy.zipCode || "")
    ].filter(Boolean);

    return addressParts.join(", ");
  };

  // Compact mode - just show the pharmacy card
  if (compact) {
    return (
      <AsyncContent height="auto" check={[loading === false]}>
        {preferredPharmacy && (
          <div>
            {!hideTitle && (
              <Title className="as-subhead2 strong">
                <Translate msg="pharmacy.yourPharmacy" />
              </Title>
            )}
            <PharmacyTile onClick={() => openSelect()}>
              <PharmacyIcon>
                <IconPharmacyStorefront />
              </PharmacyIcon>
              <PharmacyContent>
                <PharmacyName className="as-body2 m0">
                  {preferredPharmacy.pharmacyName}
                </PharmacyName>
                <PharmacyAddress className="as-little">
                  {formatPharmacyAddress()}
                </PharmacyAddress>
                <PharmacyPhone className="as-little">
                  {formatPhoneNumberNational(
                    preferredPharmacy.primaryPhone ?? "",
                    false
                  )}
                </PharmacyPhone>
              </PharmacyContent>
              <PharmacyEndIcon>
                <IconEditModify />
              </PharmacyEndIcon>
            </PharmacyTile>
            {descriptionText && (
              <p className="as-caption m1 color-c-80">{descriptionText}</p>
            )}
          </div>
        )}
      </AsyncContent>
    );
  }

  // Original full page mode
  return (
    <div>
      <OverlayBackButton show alwaysUseFallback fallbackUrl={PROFILE_URL} />
      <AppPageTitle title="Pharmacy"></AppPageTitle>

      {!preferredPharmacy && !loading && (
        <>
          <PlaceholderWrapper>
            <IconLargePlusPharmacyMedicalCupSnakeBowlOfHygieia />
            <p className="color-c-60 as-subhead strong m0">
              <Translate msg="pharmacy.notAdded" />
            </p>
          </PlaceholderWrapper>

          <nine-spacer s={"sm"}></nine-spacer>
          <NineActions style={{ justifyContent: "center" }}>
            <NineButton onClick={() => openSelect()}>
              <Translate msg="pharmacy.add" />
            </NineButton>
          </NineActions>
        </>
      )}

      <AsyncContent height="20rem" check={[loading === false]}>
        {preferredPharmacy && (
          <Data.Table
            title={<>My retail pharmacy</>}
            action={{ onAction: openSelect }}
          >
            <Data.Row title="Name" data={preferredPharmacy.pharmacyName} />
            <Data.Row
              title="Street"
              data={toTitleCase(preferredPharmacy.address1)}
            />
            {preferredPharmacy.address2 && (
              <Data.Row
                title="App/Suite"
                data={toTitleCase(preferredPharmacy.address2)}
              />
            )}
            <Data.Row title="City" data={toTitleCase(preferredPharmacy.city)} />
            <Data.Row title="State" data={preferredPharmacy.state} />
            <Data.Row
              title="ZIP"
              data={formatZipCode(preferredPharmacy.zipCode)}
            />
            <Data.Row
              title="Phone"
              data={formatPhoneNumberNational(
                preferredPharmacy.primaryPhone,
                false
              )}
            />
          </Data.Table>
        )}
      </AsyncContent>
    </div>
  );
};

export default PharmacyInformationDetail;
